import React from 'react';
import {
  Paper,
  Grid,
  Typography,
  Box,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip
} from 'recharts';
import { Student } from '@/types';

interface AttendanceStatsProps {
  student: Student;
}

export const AttendanceStats: React.FC<AttendanceStatsProps> = ({ student }) => {
  if (!student) {
    return null;
  }

  // Calculate monthly attendance data
  const monthlyData = [
    { month: 'Sep', attendance: 95, absences: 5, tardies: 2 },
    { month: 'Oct', attendance: 92, absences: 8, tardies: 3 },
    { month: 'Nov', attendance: 88, absences: 12, tardies: 4 },
    { month: 'Dec', attendance: 90, absences: 10, tardies: 2 }
  ];

  const getAttendanceColor = (rate: number) => {
    if (rate >= 95) return 'success';
    if (rate >= 90) return 'warning';
    return 'error';
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Year-to-Date Statistics
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Overall Attendance Rate
                  </Typography>
                  <Typography variant="subtitle2">
                    {student.attendanceRate}%
                  </Typography>
                </Box>
                <Tooltip title={`${student.daysPresent} days present out of ${student.daysPresent + student.daysAbsent} total days`}>
                  <LinearProgress
                    variant="determinate"
                    value={student.attendanceRate}
                    color={getAttendanceColor(student.attendanceRate)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Tooltip>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Days Present
                  </Typography>
                  <Typography variant="h4">
                    {student.daysPresent}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Days Absent
                  </Typography>
                  <Typography variant="h4">
                    {student.daysAbsent}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tardies
                  </Typography>
                  <Typography variant="h4">
                    {student.tardinessCount}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip />
                    <Legend />
                    <Bar dataKey="attendance" name="Attendance %" fill="#4caf50" />
                    <Bar dataKey="absences" name="Absences" fill="#f44336" />
                    <Bar dataKey="tardies" name="Tardies" fill="#ff9800" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};